import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import AnimatedKpiCard from '../../components/common/AnimatedKpiCard';
import ChartCard from '../../components/charts/ChartCard';
import { LineChart, BarChart, DoughnutChart, EmptyChart } from '../../components/charts/AnalyticsCharts';

const fmtINR = (n) => new Intl.NumberFormat('en-IN', {
  style: 'currency', currency: 'INR', maximumFractionDigits: 0,
}).format(Number(n) || 0);

const METHOD_LABELS = {
  cash: 'Cash',
  upi: 'UPI',
  card: 'Card',
  bank_transfer: 'Bank Transfer',
  cheque: 'Cheque',
  dd: 'Demand Draft',
  online: 'Online',
  other: 'Other',
};

export default function JoinedResourceCollectionsAnalytics({ statsPath, submitPath, titleLabel = 'Collection' }) {
  const [period, setPeriod] = useState('weekly');
  const [range, setRange] = useState(() => {
    const today = new Date().toISOString().slice(0, 10);
    return { from: today, to: today };
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitMeta, setSubmitMeta] = useState({ missingAmount: '', extraAmount: '' });
  const [submitNotice, setSubmitNotice] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { period };
      if (period === 'custom') {
        params.from = range.from;
        params.to = range.to;
      }
      const r = await api.get(statsPath, { params });
      setData(r.data?.data || {});
    } catch (_) {
      setData({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const params = { period };
        if (period === 'custom') {
          params.from = range.from;
          params.to = range.to;
        }
        const r = await api.get(statsPath, { params });
        if (alive) setData(r.data?.data || {});
      } catch (_) {
        if (alive) setData({});
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [period, range.from, range.to, statsPath]);

  const summary = data?.summary || {};
  const trend = data?.trend || [];
  const methods = data?.methodBreakdown || [];
  const transactions = data?.transactions || [];
  const submission = data?.submission || null;
  const weeklyReports = data?.weeklyReports || [];

  const handleSubmitWeeklyReport = async () => {
    if (period !== 'weekly' || submission?._id) return;
    if (!window.confirm(`Submit this weekly ${titleLabel.toLowerCase()} report as confirmed?`)) return;
    setSubmitting(true);
    setSubmitNotice(null);
    try {
      const r = await api.post(submitPath, {
        period: 'weekly',
        missingAmount: Number(submitMeta.missingAmount || 0),
        extraAmount: Number(submitMeta.extraAmount || 0),
      });
      const result = r.data?.data || {};
      setSubmitNotice({
        type: result.emailDelivery?.status === 'sent' ? 'success' : 'warning',
        message: r.data?.message || `Weekly ${titleLabel.toLowerCase()} report submitted.`,
      });
      await fetchData();
      setSubmitMeta({ missingAmount: '', extraAmount: '' });
    } catch (err) {
      setSubmitNotice({
        type: 'danger',
        message: err.response?.data?.message || `Weekly ${titleLabel.toLowerCase()} report could not be submitted.`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const submissionLabel = submission?.submittedAt
    ? `Submitted on ${new Date(submission.submittedAt).toLocaleString('en-IN')}${submission.submittedByName ? ` by ${submission.submittedByName}` : ''}`
    : '';

  return (
    <div>
      {submitNotice && (
        <div className={`alert alert-${submitNotice.type} py-2 mb-3`} role="alert">
          {submitNotice.message}
        </div>
      )}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label fw-semibold small mb-1">Collection Period</label>
              <select className="form-select form-select-sm" value={period} onChange={e => setPeriod(e.target.value)}>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
            {period === 'custom' && (
              <>
                <div className="col-md-3">
                  <label className="form-label fw-semibold small mb-1">From</label>
                  <input type="date" className="form-control form-control-sm" value={range.from} onChange={e => setRange(prev => ({ ...prev, from: e.target.value }))} />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-semibold small mb-1">To</label>
                  <input type="date" className="form-control form-control-sm" value={range.to} onChange={e => setRange(prev => ({ ...prev, to: e.target.value }))} />
                </div>
              </>
            )}
            {period === 'weekly' && (
              <div className="col-md-5 ms-md-auto">
                <label className="form-label fw-semibold small mb-1 d-block">Weekly Report</label>
                <div className="d-flex flex-wrap gap-2 justify-content-md-end align-items-center">
                  {!submission?._id && (
                    <>
                      <input type="number" min="0" className="form-control form-control-sm" style={{ width: 150 }} placeholder="Missing Amount" value={submitMeta.missingAmount} onChange={e => setSubmitMeta(prev => ({ ...prev, missingAmount: e.target.value }))} />
                      <input type="number" min="0" className="form-control form-control-sm" style={{ width: 150 }} placeholder="Extra Amount" value={submitMeta.extraAmount} onChange={e => setSubmitMeta(prev => ({ ...prev, extraAmount: e.target.value }))} />
                    </>
                  )}
                  {submission?._id && (
                    <span className="badge text-bg-success">{submissionLabel}</span>
                  )}
                  <button type="button" className="btn btn-sm btn-primary" onClick={handleSubmitWeeklyReport} disabled={loading || submitting || !!submission?._id}>
                    {submitting ? 'Submitting...' : submission?._id ? 'Report Submitted' : 'Submit Weekly Report'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-6 col-md-4 col-lg-2"><AnimatedKpiCard icon="bi-cash-stack" label="Total Collection" value={fmtINR(summary.totalCollection)} loading={loading} color="#10b981" /></div>
        <div className="col-6 col-md-4 col-lg-2"><AnimatedKpiCard icon="bi-cash-coin" label="Cash" value={fmtINR(summary.cashCollection)} loading={loading} color="#22c55e" /></div>
        <div className="col-6 col-md-4 col-lg-2"><AnimatedKpiCard icon="bi-phone" label="UPI" value={fmtINR(summary.upiCollection)} loading={loading} color="#3b82f6" /></div>
        <div className="col-6 col-md-4 col-lg-2"><AnimatedKpiCard icon="bi-wallet2" label="Other Modes" value={fmtINR(summary.otherCollection)} loading={loading} color="#8b5cf6" /></div>
        <div className="col-6 col-md-4 col-lg-2"><AnimatedKpiCard icon="bi-receipt-cutoff" label="Payments" value={summary.paymentCount || 0} loading={loading} color="#f59e0b" /></div>
        <div className="col-6 col-md-4 col-lg-2"><AnimatedKpiCard icon="bi-graph-up-arrow" label="Average" value={fmtINR(summary.averageCollection)} loading={loading} color="#0ea5e9" /></div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-lg-8">
          <ChartCard title="Collection Trend" subtitle="Amount collected across the selected period" icon="bi-graph-up-arrow" iconColor="#10b981">
            {trend.length ? (
              <LineChart height={280} labels={trend.map(t => t.label)} datasets={[{ label: 'Collection', data: trend.map(t => t.amount), color: '#10b981', fill: true, pointRadius: 3 }]} yMin={0} />
            ) : <EmptyChart label="collections" themeColor="#10b981" />}
          </ChartCard>
        </div>
        <div className="col-lg-4">
          <ChartCard title="Method Breakdown" subtitle="Collection by payment method" icon="bi-pie-chart-fill" iconColor="#3b82f6">
            {methods.length ? (
              <DoughnutChart height={280} slices={methods.map(m => ({ label: m.label, value: m.value, color: m.color }))} centerLabel={fmtINR(summary.totalCollection)} centerSubLabel="Total" themeColor="#3b82f6" />
            ) : <EmptyChart label="methods" themeColor="#3b82f6" />}
          </ChartCard>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-12">
          <ChartCard title="Collection by Method" subtitle="Amount grouped by payment mode" icon="bi-bar-chart-line-fill" iconColor="#8b5cf6">
            {methods.length ? (
              <BarChart height={260} labels={methods.map(m => METHOD_LABELS[m.key] || m.label)} datasets={[{ label: 'Amount', data: methods.map(m => m.value), color: '#8b5cf6' }]} themeColor="#8b5cf6" />
            ) : <EmptyChart label="payment methods" themeColor="#8b5cf6" />}
          </ChartCard>
        </div>
      </div>

      <div className="card">
        <div className="card-header fw-semibold d-flex justify-content-between align-items-center">
          <span><i className="bi bi-table me-2" />Collection Transactions</span>
          <span className="text-muted small">{transactions.length} records</span>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-5 text-muted">No completed payments found for the selected period.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>Name</th>
                    <th>Joined ID</th>
                    <th>Course / College</th>
                    <th>Method</th>
                    <th>Receipt</th>
                    <th>Remarks</th>
                    <th className="text-end">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx, idx) => (
                    <tr key={`${tx.paymentId || tx.receiptNumber || idx}-${idx}`}>
                      <td style={{ whiteSpace: 'nowrap' }}>{tx.paymentDate ? new Date(tx.paymentDate).toLocaleDateString('en-IN') : '-'}</td>
                      <td>{tx.studentName || '-'}</td>
                      <td><code className="small">{tx.joinedNumber || '-'}</code></td>
                      <td><div>{tx.courseName || '-'}</div><small className="text-muted">{tx.collegeName || '-'}</small></td>
                      <td>{METHOD_LABELS[tx.method] || tx.method || '-'}</td>
                      <td><code className="small">{tx.receiptNumber || tx.paymentId || '-'}</code></td>
                      <td><small>{tx.remarks || '-'}</small></td>
                      <td className="text-end fw-semibold">{fmtINR(tx.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {period === 'weekly' && (
        <div className="card mt-4">
          <div className="card-header fw-semibold d-flex justify-content-between align-items-center">
            <span><i className="bi bi-calendar-week me-2" />Weekly Submitted Reports</span>
            <span className="text-muted small">{weeklyReports.length} records</span>
          </div>
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
            ) : weeklyReports.length === 0 ? (
              <div className="text-center py-5 text-muted">No weekly reports submitted yet.</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Week</th>
                      <th className="text-end">Cash</th>
                      <th className="text-end">UPI</th>
                      <th className="text-end">Total Amount</th>
                      <th className="text-end">Missing Amount</th>
                      <th className="text-end">Extra Amount</th>
                      <th>Confirmation Date</th>
                      <th>Confirmed By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weeklyReports.map((item) => (
                      <tr key={item._id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{item.from ? new Date(item.from).toLocaleDateString('en-IN') : '-'} to {item.to ? new Date(item.to).toLocaleDateString('en-IN') : '-'}</td>
                        <td className="text-end">{fmtINR(item.cashCollection)}</td>
                        <td className="text-end">{fmtINR(item.upiCollection)}</td>
                        <td className="text-end fw-semibold">{fmtINR(item.totalCollection)}</td>
                        <td className="text-end">{fmtINR(item.missingAmount)}</td>
                        <td className="text-end">{fmtINR(item.extraAmount)}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{item.submittedAt ? new Date(item.submittedAt).toLocaleString('en-IN') : '-'}</td>
                        <td>{item.submittedByName || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
